import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import {
  Image,
  List,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  SquaresFour,
  Trash,
  X,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux";
import { ProjectIdType } from "../../../types/public-types";
import { WhiteDot } from "../../ReusableComponents/SvgAndIcons/SvgIcons";
import CreateNewArtifact from "./Artifacts/CreateNewArtifact";
import DeleteArtifact from "./Artifacts/DeleteArtifacts";
import useStyles from "./Breakdown.style";

interface ArtifactReferenceImage {
  imagePath: string;
  imageName: string;
  uploadVerified: boolean;
  signedUrl: string;
}

interface Artifact {
  artifactId: string;
  projectId: string;
  artifactName: string;
  artifactDescription: string;
  category: string;
  keywords: string[];
  artifactReferenceImages: ArtifactReferenceImage[];
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

interface Props {
  projectId: ProjectIdType;
  fetchArtifacts: any;
}

const getArtifactImageUrl = (artifact: Artifact): string => {
  return artifact.artifactReferenceImages?.[0]?.signedUrl || "";
};

const formatArtifactDate = (createdAt: {
  _seconds: number;
  _nanoseconds: number;
}): string => {
  const date = new Date(createdAt._seconds * 1000);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ArtifactViewer = ({ projectId, fetchArtifacts }: Props) => {
  const classes = useStyles();
  const artifacts = useSelector((state: RootState) => state.artifact.artifacts);
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState<string>("All");
  const [isCreateNewArtifactModalOpen, setIsCreateNewArtifactModalOpen] =
    useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [artifactToDelete, setArtifactToDelete] = useState<Artifact | null>(
    null,
  );
  const artifactTypes = [
    "All",
    "Character",
    "Location",
    "Costume",
    "Prop",
    "Others",
  ];

  const filteredArtifacts = useMemo(() => {
    return artifacts.filter((artifact) => {
      const matchesSearch =
        artifact.artifactName
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        artifact.keywords.some((k) =>
          k.toLowerCase().includes(searchText.toLowerCase()),
        );
      const matchesType =
        selectedType === "All" || artifact.category === selectedType;
      return matchesSearch && matchesType;
    });
  }, [artifacts, searchText, selectedType]);

  const handleDelete = async (artifact: Artifact) => {
    setArtifactToDelete(artifact);
    setIsDeleteModalOpen(true);
  };

  const handleEdit = (id: string) => {
    console.log("Edit artifact:", id);
  };

  return (
    <>
      <div>
        <Box className={classes.rightSideTitle}>
          <Typography className={classes.dotAndText} sx={{ py: 1 }}>
            <WhiteDot />
            <span>Artifacts</span>
          </Typography>
          <IconButton
            sx={{
              color: "#6C65D9",
              padding: "4px",
              "&:hover": {
                backgroundColor: "#6C65D9",
                color: "white",
              },
            }}
            onClick={() => setIsCreateNewArtifactModalOpen(true)}
          >
            <Plus size={20} weight="bold" />
          </IconButton>
        </Box>

        <Box
          sx={{
            backgroundColor: "#252A38",
            borderRadius: "8px",
            p: 1,
            mb: 1,
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 auto" },
              minWidth: { xs: "100%", sm: "200px" },
              display: "flex",
              alignItems: "center",
              backgroundColor: "#1B1D28",
              borderRadius: "6px",
              px: 1.5,
              height: "36px",
            }}
          >
            <input
              style={{
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                color: "white",
                fontSize: "13px",
                width: "100%",
                fontFamily: "Poppins",
              }}
              placeholder="Search artifacts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <MagnifyingGlass size={18} color="#606479" />
          </Box>

          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            sx={{
              height: "36px",
              minWidth: "120px",
              backgroundColor: "#1B1D28",
              borderRadius: "6px",
              color: "white",
              fontSize: "13px",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiSelect-icon": { color: "#606479" },
            }}
          >
            {artifactTypes.map((type) => (
              <MenuItem key={type} value={type} sx={{ fontSize: "13px" }}>
                {type}
              </MenuItem>
            ))}
          </Select>

          <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
            <IconButton
              onClick={() => setViewMode("grid")}
              sx={{
                color: viewMode === "grid" ? "#6C65D9" : "#606479",
                padding: "8px",
                "&:hover": { backgroundColor: "#1B1D28" },
              }}
            >
              <SquaresFour size={20} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode("list")}
              sx={{
                color: viewMode === "list" ? "#6C65D9" : "#606479",
                padding: "8px",
                "&:hover": { backgroundColor: "#1B1D28" },
              }}
            >
              <List size={20} />
            </IconButton>
          </Box>

          <Typography
            sx={{ fontSize: "11px", color: "#8D8E94", whiteSpace: "nowrap" }}
          >
            {filteredArtifacts.length} items
          </Typography>
        </Box>

        <Box className={classes.shortListContainer}>
          {filteredArtifacts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#606479" }}>
              <Typography sx={{ fontSize: "14px" }}>
                No artifacts found
              </Typography>
            </Box>
          ) : viewMode === "grid" ? (
            <Grid container spacing={1}>
              {filteredArtifacts.map((artifact) => {
                const imageUrl = getArtifactImageUrl(artifact);
                return (
                  <Grid item xs={6} sm={4} md={3} key={artifact.artifactId}>
                    <Box
                      sx={{
                        backgroundColor: "#1B1D28",
                        borderRadius: "8px",
                        overflow: "hidden",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.02)" },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "120px",
                          cursor: imageUrl ? "pointer" : "default",
                          position: "relative",
                          overflow: "hidden",
                          backgroundColor: "#252A38",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => imageUrl && setSelectedImage(imageUrl)}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={artifact.artifactName}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Image size={40} color="#606479" />
                        )}
                      </Box>
                      <Box sx={{ p: 1 }}>
                        <Typography
                          sx={{ fontSize: "13px", fontWeight: 500, mb: 0.3 }}
                        >
                          {artifact.artifactName}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "11px", color: "#8D8E94", mb: 0.5 }}
                        >
                          {artifact.category}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.3,
                            flexWrap: "wrap",
                            mb: 0.5,
                          }}
                        >
                          {artifact.keywords.slice(0, 2).map((keyword, idx) => (
                            <Typography
                              key={idx}
                              sx={{
                                fontSize: "9px",
                                px: 0.7,
                                py: 0.2,
                                backgroundColor: "#252A38",
                                borderRadius: "4px",
                                color: "#606479",
                              }}
                            >
                              {keyword}
                            </Typography>
                          ))}
                          {artifact.keywords.length > 2 && (
                            <Typography
                              sx={{
                                fontSize: "9px",
                                px: 0.7,
                                py: 0.2,
                                backgroundColor: "#252A38",
                                borderRadius: "4px",
                                color: "#606479",
                              }}
                            >
                              +{artifact.keywords.length - 2}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "9px", color: "#8D8E94" }}
                          >
                            {formatArtifactDate(artifact.createdAt)}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.3 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(artifact.artifactId)}
                              sx={{
                                color: "#606479",
                                "&:hover": { color: "#6C65D9" },
                                padding: "4px",
                              }}
                            >
                              <PencilSimple size={14} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(artifact)}
                              sx={{
                                color: "#606479",
                                "&:hover": { color: "#ff4444" },
                                padding: "4px",
                              }}
                            >
                              <Trash size={14} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box>
              {filteredArtifacts.map((artifact) => {
                const imageUrl = getArtifactImageUrl(artifact);
                return (
                  <Box
                    key={artifact.artifactId}
                    sx={{
                      backgroundColor: "#1B1D28",
                      borderRadius: "8px",
                      mb: 0.8,
                      p: 1.2,
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "60px", md: "70px" },
                        height: { xs: "60px", md: "70px" },
                        flexShrink: 0,
                        cursor: imageUrl ? "pointer" : "default",
                        overflow: "hidden",
                        borderRadius: "6px",
                        backgroundColor: "#252A38",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => imageUrl && setSelectedImage(imageUrl)}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={artifact.artifactName}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Image size={30} color="#606479" />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", md: "14px" },
                          fontWeight: 500,
                          mb: 0.3,
                        }}
                      >
                        {artifact.artifactName}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "11px", color: "#8D8E94", mb: 0.5 }}
                      >
                        {artifact.category}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.3,
                          flexWrap: "wrap",
                          mb: 0.3,
                        }}
                      >
                        {artifact.keywords.map((keyword, idx) => (
                          <Typography
                            key={idx}
                            sx={{
                              fontSize: "9px",
                              px: 0.7,
                              py: 0.2,
                              backgroundColor: "#252A38",
                              borderRadius: "4px",
                              color: "#606479",
                            }}
                          >
                            {keyword}
                          </Typography>
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: "9px", color: "#8D8E94" }}>
                        {formatArtifactDate(artifact.createdAt)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(artifact.artifactId)}
                        sx={{
                          color: "#606479",
                          "&:hover": { color: "#6C65D9" },
                          padding: "6px",
                        }}
                      >
                        <PencilSimple size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(artifact)}
                        sx={{
                          color: "#606479",
                          "&:hover": { color: "#ff4444" },
                          padding: "6px",
                        }}
                      >
                        <Trash size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Modal open={!!selectedImage} onClose={() => setSelectedImage(null)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={() => setSelectedImage(null)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "#1B1D28",
                  color: "white",
                  "&:hover": { backgroundColor: "#252A38" },
                }}
              >
                <X size={24} />
              </IconButton>
              <img
                src={selectedImage || ""}
                alt="Full view"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  borderRadius: "10px",
                }}
              />
            </Box>
          </Box>
        </Modal>
      </div>

      <Modal
        open={isCreateNewArtifactModalOpen}
        onClose={() => setIsCreateNewArtifactModalOpen(false)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CreateNewArtifact
          handleClose={() => setIsCreateNewArtifactModalOpen(false)}
          projectId={projectId}
          onCreateSuccess={fetchArtifacts}
        />
      </Modal>

      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <DeleteArtifact
          handleClose={() => setIsDeleteModalOpen(false)}
          artifactId={artifactToDelete?.artifactId || ""}
          artifactName={artifactToDelete?.artifactName || ""}
          onDeleteSuccess={fetchArtifacts}
        />
      </Modal>
    </>
  );
};

export default ArtifactViewer;
